import {
  BROWSER_CACHE_DB_NAME,
  BROWSER_CACHE_STORE_NAME,
  BROWSER_CACHE_VERSION,
} from '../config/publicAppConfig';

const STORAGE_TIMEOUT_MS = 1500;
const STORAGE_RETRY_MS = 30000;
const MAX_CACHE_ENTRIES = 80;
const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
let databasePromise = null;
let unavailableUntil = 0;
let prunePromise = null;

function openDatabase() {
  if (typeof window === 'undefined' || !window.indexedDB || Date.now() < unavailableUntil) {
    return Promise.resolve(null);
  }
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve) => {
    let settled = false;
    let request;
    const finish = (database) => {
      if (settled) {
        database?.close();
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      resolve(database);
    };
    const timeoutId = setTimeout(() => finish(null), STORAGE_TIMEOUT_MS);
    try {
      request = window.indexedDB.open(BROWSER_CACHE_DB_NAME, BROWSER_CACHE_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(BROWSER_CACHE_STORE_NAME)) {
          request.result.createObjectStore(BROWSER_CACHE_STORE_NAME, { keyPath: 'key' });
        }
      };
      request.onsuccess = () => {
        const database = request.result;
        database.onversionchange = () => {
          database.close();
          databasePromise = null;
        };
        database.onclose = () => { databasePromise = null; };
        finish(database);
      };
      request.onerror = () => finish(null);
      request.onblocked = () => finish(null);
    } catch {
      finish(null);
    }
  }).then((database) => {
    if (!database) {
      databasePromise = null;
      unavailableUntil = Date.now() + STORAGE_RETRY_MS;
    }
    return database;
  });
  return databasePromise;
}

function runTransaction(database, mode, fallback, operation) {
  return new Promise((resolve) => {
    let settled = false;
    let transaction;
    const finish = (value = fallback) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(value);
    };
    const timeoutId = setTimeout(() => {
      finish();
      try { transaction?.abort(); } catch { /* Already completed. */ }
    }, STORAGE_TIMEOUT_MS);
    try {
      transaction = database.transaction(BROWSER_CACHE_STORE_NAME, mode);
      transaction.onerror = () => finish();
      transaction.onabort = () => finish();
      operation(transaction.objectStore(BROWSER_CACHE_STORE_NAME), transaction, finish);
    } catch {
      finish();
    }
  });
}

// Keys include operator + cache schema, never the UI release, so useful GTFS survives updates.
export async function readBrowserCacheEntry(key, ttlMs) {
  const database = await openDatabase();
  if (!database || !Number.isFinite(ttlMs) || ttlMs <= 0) return null;
  return runTransaction(database, 'readonly', null, (store, transaction, finish) => {
    const request = store.get(key);
    request.onsuccess = () => {
      const entry = request.result;
      const savedAt = Number(entry?.savedAt);
      const age = Date.now() - savedAt;
      const fresh = entry?.value != null && Number.isFinite(savedAt) && age >= 0 &&
        age <= Math.min(ttlMs, MAX_CACHE_AGE_MS);
      finish(fresh ? entry.value : null);
    };
    request.onerror = () => finish();
  });
}

export async function writeBrowserCacheEntry(key, value, sourceSavedAt) {
  const database = await openDatabase();
  if (!database || value == null) return;
  const saved = await runTransaction(database, 'readwrite', false, (store, transaction, finish) => {
    const now = Date.now();
    const sourceTimestamp = Number(sourceSavedAt);
    const savedAt = Number.isFinite(sourceTimestamp) && sourceTimestamp > 0
      ? Math.min(now, sourceTimestamp) : now;
    store.put({ key, value, savedAt });
    transaction.oncomplete = () => finish(true);
  });
  if (saved) await pruneBrowserCacheEntries();
}

export async function deleteBrowserCacheEntry(key) {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, 'readwrite', undefined, (store, transaction, finish) => {
    store.delete(key);
    transaction.oncomplete = () => finish();
  });
}

export async function pruneBrowserCacheEntries() {
  if (prunePromise) return prunePromise;
  const database = await openDatabase();
  if (!database) return;
  prunePromise = runTransaction(database, 'readwrite', undefined, (store, transaction, finish) => {
    const entries = [];
    const request = store.openCursor();
    const now = Date.now();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        const { key, savedAt } = cursor.value;
        const timestamp = Number(savedAt);
        // Old release-specific keys and expired data do not accumulate across upgrades.
        if (!String(key).includes(':cache-v2:') || !Number.isFinite(timestamp) ||
            now - timestamp > MAX_CACHE_AGE_MS || timestamp > now) {
          cursor.delete();
        } else {
          entries.push({ key, savedAt: timestamp });
        }
        cursor.continue();
      } else {
        entries.sort((a, b) => b.savedAt - a.savedAt);
        entries.slice(MAX_CACHE_ENTRIES).forEach((entry) => store.delete(entry.key));
      }
    };
    request.onerror = () => finish();
    transaction.oncomplete = () => finish();
  }).finally(() => { prunePromise = null; });
  return prunePromise;
}
